import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { gruposItensApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import type { EssencialOpcional, GrupoItem, GrupoItemRegra } from '../../types'

/** Cria ou edita um grupo: nome, essencial padrão e regras que aceitam ou recusam termos. */
export function GrupoModal({ grupo, onClose, onSalvo }: { grupo: GrupoItem | null; onClose: () => void; onSalvo?: (g: GrupoItem) => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [nome, setNome] = useState(grupo?.nome ?? '')
  const [essencial, setEssencial] = useState<EssencialOpcional>(grupo?.essencial ?? 0)
  const [regras, setRegras] = useState<GrupoItemRegra[]>(grupo?.regras.length ? grupo.regras : [{ tipo: 1, termos: '' }])
  const [erro, setErro] = useState<string | null>(null)

  const alterar = (i: number, r: Partial<GrupoItemRegra>) => setRegras((rs) => rs.map((x, k) => (k === i ? { ...x, ...r } : x)))
  const limpas = regras.map((r) => ({ ...r, termos: r.termos.split('|').map((t) => t.trim()).filter(Boolean).join('|') })).filter((r) => r.termos)
  const frase = (tipo: 1 | 2) => limpas.filter((r) => r.tipo === tipo).map((r) => r.termos.split('|').join(' e '))
  const aceita = frase(1)
  const recusa = frase(2)
  const resumo = [aceita.length ? `Pega o item que tiver ${aceita.join(', ou ')}` : '', recusa.length ? `mas não o que tiver ${recusa.join(', nem ')}` : ''].filter(Boolean).join(', ')
  const pode = nome.trim().length > 0 && limpas.some((r) => r.tipo === 1)

  const salvar = useMutation({
    mutationFn: () => {
      const dados = { nome: nome.trim(), essencial, regras: limpas }
      return grupo ? gruposItensApi.atualizar(grupo.id, dados) : gruposItensApi.criar(dados)
    },
    onSuccess: (g) => {
      qc.invalidateQueries({ queryKey: ['itens-grupos'] })
      qc.invalidateQueries({ queryKey: ['itens-grupo-detalhe'] })
      qc.invalidateQueries({ queryKey: ['itens-sem-grupo'] })
      toast.success(grupo ? 'Grupo atualizado.' : 'Grupo criado.')
      onSalvo?.(g)
      onClose()
    },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title={grupo ? `Editar grupo · ${grupo.nome}` : 'Novo grupo de itens'} size="lg"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!pode} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button></>}
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Nome do grupo" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Leite em pó" autoFocus />
          <Select label="Padrão do grupo" value={essencial} onChange={(e) => setEssencial(Number(e.target.value) as EssencialOpcional)}>
            <option value={0}>Sem classificação</option>
            <option value={1}>Essencial</option>
            <option value={2}>Opcional</option>
          </Select>
        </div>

        <div className="space-y-2">
          <span className="form-label">Regras</span>
          <p className="text-2xs text-muted">Numa regra, todos os termos separados por | precisam aparecer. Para &quot;ou&quot;, adicione outra regra.</p>
          <p className="text-2xs text-muted">Exemplo: a regra <span className="font-mono text-white">LEITE|PO</span> pega &quot;LEITE INTEGRAL EM PO&quot;, mas não &quot;LEITE UHT&quot;. Para pegar também &quot;LEITE NINHO&quot;, adicione a regra <span className="font-mono text-white">NINHO</span>. Uma regra que recusa afasta o item (ex.: CONDENSADO). O grupo com mais termos casados vence.</p>
          {regras.map((r, i) => (
            <div key={i} className="grid items-end gap-2 sm:grid-cols-[9rem_1fr_auto]">
              <Select label={i === 0 ? 'Tipo' : ''} value={r.tipo} onChange={(e) => alterar(i, { tipo: Number(e.target.value) as 1 | 2 })} aria-label={`Tipo da regra ${i + 1}`}>
                <option value={1}>Aceita</option>
                <option value={2}>Recusa</option>
              </Select>
              <Input label={i === 0 ? 'Termos' : ''} value={r.termos} onChange={(e) => alterar(i, { termos: e.target.value })} placeholder="LEITE|PO" aria-label={`Termos da regra ${i + 1}`} className="font-mono uppercase" />
              <IconButton icon="ri-delete-bin-line" label={`Remover regra ${i + 1}`} onClick={() => setRegras((rs) => rs.filter((_, k) => k !== i))} />
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="light" icon="ri-add-line" onClick={() => setRegras((rs) => [...rs, { tipo: 1, termos: '' }])}>{regras.some((r) => r.tipo === 1) ? 'Outra regra (ou)' : 'Regra que aceita'}</Button>
            <Button size="sm" variant="light" icon="ri-forbid-line" onClick={() => setRegras((rs) => [...rs, { tipo: 2, termos: '' }])}>Regra que recusa</Button>
          </div>
          {resumo && <p className="rounded bg-surface2 px-3 py-2 text-13 text-white">{resumo}</p>}
        </div>
        {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
      </div>
    </Modal>
  )
}
